import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";

const QUOTE =
  "Create a market report and send it to these 3 clients. When our KB gets new data that matches their interests, automatically send them updates. Track their responses, analyze sentiment, flag actions needed.";

const FLOW_STEPS = [
  { label: "Intent",     color: COLORS.neonPink   },
  { label: "Decompose",  color: COLORS.neonBlue   },
  { label: "Execute",    color: COLORS.neonPurple  },
  { label: "Monitor",    color: COLORS.neonGreen   },
  { label: "Report",     color: COLORS.neonOrange  },
];

const BG_ORBS = [
  { x: 200,  y: 200,  size: 400, color: COLORS.neonPurple, freq: 0.02, phase: 0   },
  { x: 1600, y: 800,  size: 350, color: COLORS.neonBlue,   freq: 0.015, phase: 1  },
  { x: 900,  y: 600,  size: 500, color: COLORS.neonPink,   freq: 0.025, phase: 2  },
];

export const Scene02Vision: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // Typing animation: reveal characters over first 120 frames
  const charsToShow = Math.floor(
    interpolate(frame, [20, 130], [0, QUOTE.length], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    }),
  );
  const displayedQuote = QUOTE.slice(0, charsToShow);
  const showCursor = frame < 140 || Math.floor(frame * 0.1) % 2 === 0;

  // Title
  const titleAppear = spring({ frame, fps, config: SPRING.gentle });

  // Card 3D tilt
  const cardTiltX = interpolate(Math.sin(frame * 0.02), [-1, 1], [-1, 1]);
  const cardTiltY = interpolate(Math.cos(frame * 0.015), [-1, 1], [-1.5, 1.5]);

  // "User describes intent" text
  const intentAppear = spring({ frame: frame - 180, fps, config: SPRING.bouncy });

  // Flow steps stagger
  const flowStepSprings = FLOW_STEPS.map((_, i) =>
    spring({ frame: frame - (100 + i * 20), fps, config: SPRING.snappy }),
  );

  // Bottom text
  const bottomAppear = spring({ frame: frame - 220, fps, config: SPRING.punch });

  // Exit fade
  const exitOpacity = interpolate(frame, [durationInFrames - 30, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        background: COLORS.bg,
        opacity: exitOpacity,
        overflow: "hidden",
      }}
    >
      {/* Pulsing background orbs */}
      <AbsoluteFill style={{ pointerEvents: "none" }}>
        {BG_ORBS.map((orb, i) => {
          const pulse = interpolate(Math.sin(frame * orb.freq + orb.phase), [-1, 1], [0.8, 1.2]);
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                left: orb.x - (orb.size * pulse) / 2,
                top: orb.y - (orb.size * pulse) / 2,
                width: orb.size * pulse,
                height: orb.size * pulse,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${orb.color}18 0%, transparent 70%)`,
                pointerEvents: "none",
              }}
            />
          );
        })}
      </AbsoluteFill>

      {/* Parallax depth layers */}
      <div style={{
        position: "absolute",
        inset: 0,
        transform: `translateY(${Math.sin(frame * 0.008) * 8}px) translateX(${Math.cos(frame * 0.006) * 5}px)`,
        pointerEvents: "none",
      }}>
        {[
          { x: 150, y: 200, size: 400, color: COLORS.neonPurple, speed: 0.005, phase: 0 },
          { x: 1600, y: 300, size: 350, color: COLORS.neonBlue, speed: 0.004, phase: 2 },
          { x: 900, y: 800, size: 500, color: COLORS.neonPink, speed: 0.003, phase: 4 },
        ].map((orb, i) => (
          <div key={`parallax-${i}`} style={{
            position: "absolute",
            left: orb.x + Math.sin(frame * orb.speed + orb.phase) * 30 - orb.size / 2,
            top: orb.y + Math.cos(frame * orb.speed * 0.7 + orb.phase) * 20 - orb.size / 2,
            width: orb.size,
            height: orb.size,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${orb.color}06 0%, transparent 70%)`,
          }} />
        ))}
      </div>

      {/* Mid-layer floating particles */}
      {[...Array(6)].map((_, i) => {
        const px = 200 + i * 300;
        const py = 150 + (i % 3) * 300;
        const pColor = [COLORS.neonPink, COLORS.neonBlue, COLORS.neonGreen, COLORS.neonOrange, COLORS.neonPurple, COLORS.neonCyan][i];
        return (
          <div key={`particle-mid-${i}`} style={{
            position: "absolute",
            left: px + Math.sin(frame * 0.02 + i * 1.5) * 15,
            top: py + Math.cos(frame * 0.015 + i * 2) * 12,
            width: 4 + (i % 3) * 2,
            height: 4 + (i % 3) * 2,
            borderRadius: "50%",
            background: pColor,
            boxShadow: `0 0 8px ${pColor}80, 0 0 16px ${pColor}40`,
            opacity: 0.5 + Math.sin(frame * 0.03 + i) * 0.2,
            pointerEvents: "none",
          }} />
        );
      })}

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 60,
          left: 0,
          right: 0,
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
          transform: `translateY(${interpolate(titleAppear, [0, 1], [-20, 0])}px)`,
        }}
      >
        <span
          style={{
            fontSize: 44,
            fontFamily: FONTS.heading,
            fontWeight: 800,
            background: `linear-gradient(135deg, ${COLORS.neonPurple}, ${COLORS.neonBlue})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          The Vision
        </span>
      </div>

      {/* Left: 3D tilted card with typing animation */}
      <div
        style={{
          position: "absolute",
          top: 150,
          left: 80,
          width: 900,
          bottom: 200,
          perspective: "1000px",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            background: COLORS.bgCard,
            borderRadius: 16,
            border: `1px solid ${COLORS.neonPurple}40`,
            padding: "40px 44px",
            boxSizing: "border-box",
            transform: `rotateY(${-5 + cardTiltY}deg) rotateX(${3 + cardTiltX}deg) translateZ(${Math.sin(frame * 0.01) * 3}px)`,
            boxShadow: `0 0 40px ${COLORS.neonPurple}30, 0 0 80px ${COLORS.neonPurple}15, inset 0 0 40px ${COLORS.neonPurple}08`,
          }}
        >
          {/* Card header bar */}
          <div style={{ display: "flex", gap: 8, marginBottom: 28 }}>
            {[COLORS.neonRed, COLORS.neonYellow, COLORS.neonGreen].map((c, i) => (
              <div
                key={i}
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: c,
                  boxShadow: `0 0 8px ${c}`,
                }}
              />
            ))}
            <span
              style={{
                marginLeft: 12,
                fontSize: 13,
                fontFamily: FONTS.mono,
                color: COLORS.textMuted,
              }}
            >
              user-request.txt
            </span>
          </div>

          {/* Typing text */}
          <p
            style={{
              fontSize: 20,
              fontFamily: FONTS.mono,
              color: COLORS.textPrimary,
              lineHeight: 1.75,
              margin: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {displayedQuote}
            {showCursor && (
              <span
                style={{
                  display: "inline-block",
                  width: 2,
                  height: "1.1em",
                  background: COLORS.neonCyan,
                  marginLeft: 2,
                  verticalAlign: "text-bottom",
                  boxShadow: `0 0 8px ${COLORS.neonCyan}`,
                }}
              />
            )}
          </p>
        </div>
      </div>

      {/* "User describes intent" banner */}
      <div
        style={{
          position: "absolute",
          bottom: 100,
          left: 80,
          width: 900,
          opacity: interpolate(intentAppear, [0, 1], [0, 1]),
          transform: `translateY(${interpolate(intentAppear, [0, 1], [20, 0])}px)`,
          textAlign: "center",
        }}
      >
        <span
          style={{
            fontSize: 28,
            fontFamily: FONTS.body,
            fontWeight: 700,
            color: COLORS.neonGreen,
            textShadow: `0 0 20px ${COLORS.neonGreen}80, 0 0 40px ${COLORS.neonGreen}40`,
          }}
        >
          User describes intent. EAKC handles execution.
        </span>
      </div>

      {/* Right: animated flow steps */}
      <div
        style={{
          position: "absolute",
          top: 150,
          right: 80,
          width: 380,
          display: "flex",
          flexDirection: "column",
          gap: 0,
          alignItems: "center",
        }}
      >
        {FLOW_STEPS.map((step, i) => {
          const s = flowStepSprings[i];
          const opacity = interpolate(s, [0, 1], [0, 1]);
          const translateY = interpolate(s, [0, 1], [20, 0]);
          const scale = interpolate(s, [0, 1], [0.8, 1]);

          return (
            <React.Fragment key={i}>
              <div
                style={{
                  opacity,
                  transform: `translateY(${translateY}px) scale(${scale})`,
                  width: "100%",
                }}
              >
                <div
                  style={{
                    background: COLORS.bgCard,
                    border: `2px solid ${step.color}`,
                    borderRadius: 12,
                    padding: "18px 24px",
                    textAlign: "center",
                    boxShadow: `0 0 20px ${step.color}40, 0 0 40px ${step.color}20`,
                  }}
                >
                  <span
                    style={{
                      fontSize: 20,
                      fontFamily: FONTS.heading,
                      fontWeight: 700,
                      color: step.color,
                      textShadow: `0 0 12px ${step.color}80`,
                    }}
                  >
                    {step.label}
                  </span>
                </div>
              </div>

              {/* Animated dashed connector */}
              {i < FLOW_STEPS.length - 1 && (
                <div
                  style={{
                    opacity,
                    width: 2,
                    height: 32,
                    background: `repeating-linear-gradient(to bottom, ${step.color}80 0px, ${step.color}80 6px, transparent 6px, transparent 12px)`,
                    backgroundPositionY: `${(frame * 2) % 12}px`,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Bottom tagline */}
      <div
        style={{
          position: "absolute",
          bottom: 50,
          right: 80,
          width: 380,
          opacity: interpolate(bottomAppear, [0, 1], [0, 1]),
          transform: `scale(${interpolate(bottomAppear, [0, 1], [0.85, 1])})`,
          textAlign: "center",
        }}
      >
        <span
          style={{
            fontSize: 22,
            fontFamily: FONTS.heading,
            fontWeight: 800,
            color: COLORS.neonYellow,
            textShadow: `0 0 20px ${COLORS.neonYellow}80, 0 0 40px ${COLORS.neonYellow}40`,
          }}
        >
          Power Apps without code
        </span>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
